// Mapping of flowsheet item names (vitals or lab components) to categories/organ systems
// Keys should match the 'name' field returned by the backend flowsheetData endpoint
export const flowsheetItemCategories = {
  // Vitals (Updated List - Using User-Friendly Display Names)
  'Systolic Blood Pressure': 'Vitals',
  'Diastolic Blood Pressure': 'Vitals',
  'Mean Arterial Pressure (MAP)': 'Vitals',
  'Pulse Pressure (PP)': 'Vitals',
  'Heart Rate': 'Vitals',
  'Respiratory Rate': 'Vitals',
  'Temperature': 'Vitals',
  'Oxygen Saturation': 'Vitals',
  'SIRS Score': 'Vitals',
  'Heart Rate Variability': 'Vitals',
  'VO2 Max': 'Vitals',
  'Pain Level': 'Vitals',
  'Weight': 'Vitals',
  'Height': 'Vitals',
  'BMI': 'Vitals',
  // Original Vitals Keys (Commented out - mapping handled in PatientFlowsheet.js)
  // 'temperature': 'Vitals',
  // 'heartRate': 'Vitals',
  // 'respiratoryRate': 'Vitals',
  // 'bloodPressureSystolic': 'Vitals',
  // 'bloodPressureDiastolic': 'Vitals',
  // 'oxygenSaturation': 'Vitals',
  // 'weight': 'Vitals', // Now explicitly defined above
  // 'height': 'Vitals', // Now explicitly defined above

  // Hematology - WBC
  'WBC': 'CBC',
  'Neutrophil': 'CBC', // Canonical name for Absolute Neutrophil Count
  'Lymphocyte': 'CBC', // Canonical name for Absolute Lymphocyte Count
  'Monocyte': 'CBC',   // Canonical name for Absolute Monocyte Count
  'Eosinophil': 'CBC', // Canonical name for Absolute Eosinophil Count
  'Basophil': 'CBC',   // Canonical name for Absolute Basophil Count
  'Neutrophil %': 'CBC',
  'Lymphocyte %': 'CBC',
  'Monocyte %': 'CBC',
  'Eosinophil %': 'CBC',
  'Basophil %': 'CBC',
  'NLR': 'CBC', // Neutrophil-to-Lymphocyte Ratio

  // Hematology - RBC
  'RBC': 'CBC',
  'HGB': 'CBC', // Canonical name for Hemoglobin
  'HCT': 'CBC', // Canonical name for Hematocrit
  'MCV': 'CBC',
  'MCH': 'CBC',
  'MCHC': 'CBC',
  'RDW': 'CBC',
  'RDW-SD': 'CBC',

  // Hematology - Platelet
  'PLT': 'CBC', // Canonical name for Platelet Count
  'MPV': 'CBC',
  'PDW': 'CBC',
  'P-LCR': 'CBC', // Added missing P-LCR parameter

  // Liver Function
  'AST': 'Hepatology', 
  'ALT': 'Hepatology',
  'ALP': 'Hepatology', // Canonical name for Alkaline Phosphatase
  'Bilirubin, Total': 'Hepatology',    // Canonical name
  'Bilirubin, Direct': 'Hepatology',   // Canonical name
  'Bilirubin Indirect': 'Hepatology', // Canonical name
  'GGT': 'Hepatology', // Added GGT as it's often part of liver panels
  'Albumin': 'Protein', 
  'Total Protein': 'Protein', 
  'Albumin/Globulin Ratio': 'Protein',

  // Renal Function
  'BUN': 'Renal',
  'Creatinine': 'Renal',
  'eGFR': 'Renal',
  'BUN/Creatinine Ratio': 'Renal',
  'Urea': 'Renal',
  'Osmolality': 'Renal',

  // Metabolic / Electrolytes / Glucose
  'Sodium': 'Electrolytes',
  'Potassium': 'Electrolytes',
  'Chloride': 'Electrolytes',
  'Bicarbonate-CO2': 'Electrolytes', // Canonical name
  'Sodium/Potassium Ratio': 'Electrolytes',
  'Glucose (Serum)': 'Glucose',
  'Calcium': 'Electrolytes',
  'Calcium, Ionized': 'Electrolytes', // Added missing Calcium, Ionized parameter
  // BUN, Creatinine are already under Renal
  // Albumin, Total Protein are under Protein

  // Cholesterol Panel
  'Cholesterol Total': 'Lipid',
  'Triglyceride': 'Lipid',
  'HDL': 'Lipid',
  'LDL': 'Lipid',
  'VLDL': 'Lipid',
  'Apo B': 'Lipid',
  'TC/HDL Ratio': 'Lipid',
  'non-HDL Cholesterol': 'Lipid',
  'TC/GLU Ratio': 'Lipid',
  'TG/HDL Ratio': 'Lipid',
  'Atherogenic Index': 'Lipid',

  // Thyroid
  'TSH': 'Thyroid',

  // Imaging
  'Chest XR': 'Imaging',
  'Carotid US': 'Imaging',
  'Thyroid US': 'Imaging',
  'Heart US': 'Imaging',
  'Liver US': 'Imaging',
  'Gallbladder US': 'Imaging',
  'Pancreas US': 'Imaging',
  'Spleen US': 'Imaging',
  'Kidney US': 'Imaging',
  'Bladder US': 'Imaging', // General entry, specific params below

  // Bladder Ultrasound Specific Parameters
  'imaging_bladder_us_pre_void_volume': 'Imaging',
  'imaging_bladder_us_pvr_volume': 'Imaging',
  'imaging_bladder_us_wall_thickness': 'Imaging',
  'imaging_bladder_us_calculi_presence': 'Imaging',
  'imaging_bladder_us_calculi_description': 'Imaging',
  'imaging_bladder_us_masses_presence': 'Imaging',
  'imaging_bladder_us_masses_description': 'Imaging',
  'imaging_bladder_us_ureteral_jets': 'Imaging',

  'Free T4': 'Thyroid',
  'Free T3': 'Thyroid',

  // Urine (Example components)
  'Specific Gravity': 'Urine',
  'pH': 'Urine',
  'Color': 'Urine',
  'Clarity': 'Urine',
  'Nitrite': 'Urine',
  'Protein': 'Urine', // For urine dipstick protein
  'Glucose (Urine)': 'Urine',
  'Ketones': 'Urine',
  'Urobilinogen': 'Urine',
  'Bilirubin': 'Urine', // For urine dipstick bilirubin
  'Erythrocyte': 'Urine', // For urine dipstick erythrocytes/blood
  'Leukocyte': 'Urine', // For urine dipstick leukocyte esterase
  'Microalbumin': 'Urine',
  'Creatinine (Urine)': 'Urine',
  'UACR': 'Urine',

  // Coagulation
  'PT': 'Coagulation',
  'INR': 'Coagulation',
  'PTT': 'Coagulation',

  // Cardiac
  'Troponin-HS': 'Cardiac', // Canonical name updated
  'CK-MB': 'Cardiac',
  'BNP': 'Cardiac',

  // Vitamins
  'Vitamin D3': 'Vitamins',
  'Vitamin D, 25-Hydroxy': 'Vitamins', // Added missing Vitamin D, 25-Hydroxy parameter

  // Health Surveys
  'Health Survey (Link)': 'Surveys',
  'Health Survey': 'Surveys',
  // Add other categories: Immune, Inflammatory, etc.
};

// Function to get category for an item name
export const getCategoryForItem = (itemName, providedCategory, itemType) => {
  // 1. Prioritize the category provided directly from the backend data
  //    (This covers Labs, Diagnostics, Questionnaires, Scores)
  if (providedCategory && providedCategory !== 'Uncategorized' && providedCategory !== 'Physical Exam' && providedCategory !== 'Lab Results') { // Ignore generic categories from backend
    // Check if the provided category is a known lab sub-category, otherwise use the mapping
     const knownLabSubCategories = ['CBC', 'Hepatology', 'Renal', 'Electrolytes', 'Glucose', 'Lipid', 'Thyroid', 'Urinalysis', 'Coagulation', 'Cardiac', 'Protein', 'Vitamins', 'Inflammation', 'Urine', 'Hematology - WBC', 'Hematology - RBC', 'Hematology - Platelet', 'Liver Function', 'Metabolic / Electrolytes / Glucose', 'Cholesterol Panel']; // Added missing lab categories based on flowsheetItemCategories
     if (knownLabSubCategories.includes(providedCategory)) {
         return providedCategory;
     }
  }

  // 2. Use the mapping for known Vitals or other non-lab parameters
  if (flowsheetItemCategories[itemName]) {
      return flowsheetItemCategories[itemName];
  }

  // 3. For Lab parameters, enforce standardized naming - NO FALLBACKS
  if (itemType === 'Lab') {
    console.error(`Lab parameter '${itemName}' does not follow standardized naming convention. All lab parameters must use standardized names from parameters.md`);
    return 'Uncategorized'; // Make it obvious that standardization is missing
  }

  // 4. Handle Physical Exam types specifically (category assigned during transformation)
  if (itemType === 'Physical Exam Summary') return 'PE Summaries'; // Should be assigned in transform
  if (itemType === 'Physical Exam Detail') return 'Unknown Exam Section'; // Should be assigned in transform

  // 5. Handle other known types without specific mapping
  if (itemType === 'Diagnostic') return 'Imaging'; // Renamed from Diagnostic Studies
  if (itemType === 'Score') return 'Lifestyle Scores'; // Keep old mapping for now, will be handled in PatientFlowsheet transformation
  if (itemType === 'Questionnaire') return 'Lifestyle Questionnaire'; // Keep old mapping for now, will be handled in PatientFlowsheet transformation
  if (itemType === 'Survey') return 'Surveys'; // Health Survey data

  // 6. Handle specific items that need a category assigned when none is provided
  if (itemName === 'painLevel' && itemType === 'Vital') {
      return 'Vitals';
  }

  // 7. Default fallback
  console.warn(`Could not determine category for item: ${itemName} (Type: ${itemType}, Provided: ${providedCategory})`);
  return 'Other';
};

// Define the desired order of TOP-LEVEL categories and SUB-MENU categories for display
// Sub-menu items will be sorted based on their order within physicalExamSectionsFull or alphabetically for labs.
export const categoryDisplayOrder = [
    'Vitals',
    'Physical Exam', // Parent SubMenu Key
    'Biometrics',    // Parent SubMenu Key (NEW)
    'Lab Results',   // Parent SubMenu Key
    'Imaging', // Renamed from Diagnostic Studies
    'Lifestyle', // New Parent SubMenu Key
    'Surveys', // Health Survey data
    'Other', // Added 'Other' category
];

// Note: The sub-categories for 'Lifestyle' will be dynamically generated in PatientFlowsheet.js
// based on the structure defined in lifestyleCategories.js.
// Existing data mapped to old Lifestyle categories (e.g., 'Lifestyle Scores', 'Lifestyle Questionnaire')
// will need to be re-mapped to the new subcategories in PatientFlowsheet.js's transformedData logic.

// Note: The actual sub-categories for 'Physical Exam' and 'Lab Results'
// will be dynamically generated and sorted within PatientFlowsheet.js
// based on the data present and the structure of physicalExamSectionsFull.
